import React from 'react';
import { Container, Grid, Title, Text, Card, Group, Button, List, ThemeIcon, Box } from '@mantine/core';
import { IconHeartbeat, IconInfoCircle, IconCheck, IconArrowRight } from '@tabler/icons-react';
import InfoCard from '../components/InfoCard';
import ChartComponent from '../components/ChartComponent';
import CommunityResourcesCard from '../components/CommunityResourcesCard';
import {
  symptomData, ageData, statistics,
  triggerNames
} from '../data';
import TriggerCounter from '../components/TriggerCounter';
import SourcesCard from '../components/SourcesCard';
import FAQCard from '../components/FAQCard';
import { trackNavigation, trackEngagement } from '../utils/tracking';

const scrollToKeyFacts = () => {
  trackNavigation('hero_to_key_facts');
  document.getElementById('key-facts')?.scrollIntoView({
    behavior: 'smooth'
  });
};

const scrollToResources = () => {
  trackNavigation('hero_to_resources');
  document.getElementById('community-resources')?.scrollIntoView({
    behavior: 'smooth'
  });
};

const handleDonationClick = () => {
  trackEngagement('buymeacoffee');
};

const MainPage: React.FC = () => {
  return (
    <Container size="xl" py="xl">
      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-content">
          <Title className="hero-title">
            Understanding Hemiplegic Migraines
          </Title>
          <Text size="lg" mb="xl" c="gray.2">
            Welcome to a community resource website dedicated to hemiplegic migraines.
            Created out of frustration with the lack of accessible information about this
            rare but debilitating condition.
          </Text>

          <Text size="md" mb="lg" c="gray.3">
            Hemiplegic migraine is a rare form of migraine that goes beyond a typical migraine.
            Unlike regular migraines, it can cause temporary paralysis or weakness on one side of the body,
            and can have stroke-like symptoms including speech difficulties, vision problems, and confusion.
            These frightening symptoms often lead to emergency room visits which is what happend to me personally, and in some cases it can be misdiagnosed as strokes.
          </Text>

          <Text size="md" mb="xl" c="gray.3">
            If you're here because you or someone you care about has been affected by hemiplegic migraines,
            you're not alone. This site aims to provide the clear information, community support, and
            resources that are often so hard to find elsewhere.
          </Text>

          <Text size="sm" mb="xl" c="gray.4" style={{ fontStyle: 'italic', borderTop: '1px solid #444', paddingTop: '16px' }}>
            <strong>Important Disclaimer:</strong> This site is not intended to provide medical advice, but rather to offer support systems and educational information. Always consult with qualified healthcare professionals for proper diagnosis and treatment. If you're experiencing stroke-like symptoms, seek immediate medical attention.
          </Text>
          <Group>
            <Button
              size="md"
              radius="md"
              onClick={scrollToKeyFacts}
              gradient={{ from: 'indigo', to: 'cyan' }}
              variant="gradient"
            >
              <Group gap="xs">
                <IconInfoCircle size={18} />
                <span>Learn More</span>
              </Group>
            </Button>
            <Button
              size="md"
              radius="md"
              onClick={scrollToResources}
              variant="outline"
              color="gray"
            >
              <Group gap="xs">
                <IconHeartbeat size={18} />
                <span>Been Diagnosed? Find Support</span>
              </Group>
            </Button>
          </Group>

        </div>
      </div>


      <Box mt={60} mb={30} id="key-facts">
        <Title order={2} mb={30} fw={700} ta="center" c="gray.1">
          Key Facts About Hemiplegic Migraines
        </Title>
        <Grid>
          {statistics.map((stat, index) => (
            <Grid.Col span={{ base: 12, sm: 6, md: 4 }} key={index}>
              <InfoCard
                title={stat.title}
                value={stat.value}
                description={stat.description}
              />
            </Grid.Col>
          ))}
        </Grid>
      </Box>


      <Box mt={60} mb={30}>
        <Title order={2} mb={30} fw={700} ta="center" c="gray.1">
          Hemiplegic Migraine Statistics
        </Title>
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <ChartComponent
              type="bar"
              data={symptomData}
              title="Common Symptoms Estimates (%)"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <ChartComponent
              type="pie"
              data={ageData}
              title="Age Distribution (%)"
            />
          </Grid.Col>
        </Grid>

      </Box>
      <SourcesCard />

      <Box mt={60} mb={30}>
        <Title order={2} mb={30} fw={700} ta="center" c="gray.1">
          Common Triggers
        </Title>
        <Card shadow="md" p="xl" radius="lg" withBorder className="modern-card">
          <Title order={3} mb="xl" c="gray.1">What Triggers Hemiplegic Migraines?</Title>
          <Text mb="xl" c="gray.3">
            Identifying and avoiding triggers is one of the most effective ways to reduce
            the frequency of hemiplegic migraine attacks. These are some of the most commonly reported:
          </Text>
          <List
            spacing="md"
            size="md"
            center
            icon={
              <ThemeIcon color="indigo" size={28} radius="xl">
                <IconCheck size={18} />
              </ThemeIcon>
            }
          >
            {triggerNames.map((trigger, index) => (
              <List.Item key={index} className="animated-item" style={{ animationDelay: `${index * 0.1}s` }}>
                <Text c="gray.1">{trigger}</Text>
              </List.Item>
            ))}
          </List>
          <TriggerCounter />
        </Card>
      </Box>

      {/* FAQ Section */}
      <Box mt={60} mb={30} id="faq">
        <Title order={2} mb={30} fw={700} ta="center" c="gray.1">
          Frequently Asked Questions
        </Title>
        <FAQCard />
      </Box>

      {/* Community Resources Section */}
      <Box mt={60} mb={30} id="community-resources">
        <Title order={2} mb={30} fw={700} ta="center" c="gray.1">
          Community Resources
        </Title>
        <CommunityResourcesCard />
      </Box>


      <div className="cta-section">
        <div className="cta-content">
          <Title order={2} mb="md" c="gray.1">Support This Resource</Title>
          <Text size="lg" mb="xl" c="gray.3">
            This website was created to provide much-needed information on hemiplegic migraines.
            Help me continue to maintain and expand this resource for the community.
          </Text>
          <Group justify="center">
            <Button
              component="a"
              href="https://www.buymeacoffee.com/joedst"
              target="_blank"
              onClick={handleDonationClick}
              size="lg"
              radius="md"
              gradient={{ from: 'indigo', to: 'cyan' }}
              variant="gradient"
            >
              <Group gap="xs">
                <span>Buy Me a Coffee</span>
                <IconArrowRight size={18} />
              </Group>
            </Button>
          </Group>
        </div>
      </div>
    </Container>
  );
};

export default MainPage;